import { notFound } from "@tanstack/react-router";
import { createServerFn } from "@tanstack/react-start";
import { isExperiencePreview, listPublicExperiences } from "../../../api/src/experiences/service";
import { listPublishedCreatorPages } from "../../../api/src/pages/service";
import { readMatchupGuide, readMatchupLibrary } from "./matchups.server";
import { readMatchupRoute } from "./matchup-route.server";
import {
  privateResponse,
  publicationResponse,
  shareableResponse,
  siteContext,
} from "./site.server";

/**
 * The Matchups feature's shared data (routes/matchups.tsx): the library's discovery list and
 * heading, and whether the creator composed its index page. Guides and the index read from it, so
 * moving between them never asks for the list again. A signed-out visitor's list is the same for
 * everyone and may be kept at the edge; a member's or an owner's preview never is.
 */
export const loadMatchups = createServerFn({ method: "GET" }).handler(async ({ context }) => {
  const drate = await siteContext(context);
  publicationResponse(drate);
  if (drate.user || isExperiencePreview(drate)) privateResponse();
  else shareableResponse();
  const experience = (await listPublicExperiences(drate)).find((item) => item.kind === "matchups");
  if (!experience) {
    const pages = drate.templatePreview ? [] : await listPublishedCreatorPages(drate);
    return pages.some((page) => page.path === "/matchups" || page.path.startsWith("/matchups/"))
      ? { kind: "pages" as const, composedIndex: true }
      : null;
  }
  const [library, pages] = await Promise.all([
    readMatchupLibrary(drate, experience),
    drate.templatePreview ? [] : listPublishedCreatorPages(drate),
  ]);
  return {
    kind: "library" as const,
    ...library,
    composedIndex: pages.some((page) => page.path === "/matchups"),
  };
});

/**
 * One guide, authorized afresh on every visit; a members-only guide answers a locked preview to
 * anyone who can't read it. A signed-out visitor's guide (a public guide, or a locked preview with
 * discovery fields only) may be kept at the edge; a signed-in visitor's guide or an owner's preview
 * never is. Publishing a guide bumps the website revision, which keys the edge copy. When the
 * creator designed a guide template, it comes along, bound to this guide's discovery fields; a
 * template that differs by visitor keeps the page private.
 */
export const loadMatchupGuide = createServerFn({ method: "GET" })
  .validator((input: unknown) => {
    const { slug, section } = (input ?? {}) as { slug?: unknown; section?: unknown };
    if (typeof slug !== "string" || !/^[a-z0-9-]{1,120}$/.test(slug)) throw notFound();
    if (section !== undefined && (typeof section !== "string" || section.length > 40))
      throw notFound();
    return { slug, section: section as string | undefined };
  })
  .handler(async ({ data, context }) => {
    const drate = await siteContext(context);
    const guide = await readMatchupRoute(drate, data.slug, data.section);
    if (!guide || drate.user || isExperiencePreview(drate)) privateResponse();
    else shareableResponse();
    publicationResponse(drate);
    return guide;
  });
export type LoadedMatchupGuide = Extract<
  Awaited<ReturnType<typeof loadMatchupGuide>>,
  { kind: "guide" }
>;

/**
 * The game plan a live matchup found for this visitor's lane: the opponent's guide, authorized
 * afresh like the guide page, so a members-only guide comes back as its locked preview. It follows
 * one visitor's game, so it is never kept at the edge, whoever asks.
 */
export const loadLiveMatchupGuide = createServerFn({ method: "GET" })
  .validator((input: unknown) => {
    const { slug } = (input ?? {}) as { slug?: unknown };
    if (typeof slug !== "string" || !/^[a-z0-9-]{1,120}$/.test(slug)) throw notFound();
    return { slug };
  })
  .handler(async ({ data, context }) => {
    privateResponse();
    const drate = await siteContext(context);
    const experience = (await listPublicExperiences(drate)).find(
      (item) => item.kind === "matchups",
    );
    if (!experience) return null;
    const [library, guide] = await Promise.all([
      readMatchupLibrary(drate, experience),
      readMatchupGuide(drate, data.slug, "overview"),
    ]);
    if (!guide) return null;
    // Embedded, the guide shows no library to jump through and no skill order.
    return {
      guide: { ...guide, layout: library.layout, skillOrder: null },
      library: { ...library, entries: [] },
    };
  });
export type LiveMatchupGuide = NonNullable<Awaited<ReturnType<typeof loadLiveMatchupGuide>>>;
